export const WORKBOOK_KEYS = [
  "serial",
  "visit_time",
  "visit_date",
  "came",
  "tasting_guests",
  "table",
  "sales",
  "wanted_date",
  "wanted_venue",
  "mail_in",
  "mail_out",
  "evaluation",
  "ok",
  "closed",
  "follow_up",
  "follow_up_manual",
  "status_note",
  "commitment",
  "contract_date",
  "deposit_date",
  "installment_1",
  "installment_2",
  "installment_3",
  "paid_off",
  "final_meeting",
  "agreed_date",
  "agreed_type",
  "agreed_venue",
  "file_code",
  "agreed_guests",
  "children_free",
  "service_venue",
  "service_time",
  "menu",
  "setup",
  "requirements",
  "dj",
  "orchestra",
  "fireworks",
  "animator",
  "games",
  "extras",
  "church",
  "allergies",
  "guest_list",
  "service_type",
  "service_notes",
] as const;

export type WorkbookKey = (typeof WORKBOOK_KEYS)[number];

const SERVICE_KEYS: WorkbookKey[] = ["service_venue", "service_time", "menu", "setup", "requirements", "dj", "orchestra", "fireworks", "animator", "games", "extras", "church", "allergies", "guest_list", "service_type", "service_notes"];
const COORDINATION_KEYS: WorkbookKey[] = ["commitment", "contract_date", "deposit_date", "installment_1", "installment_2", "installment_3", "paid_off", "final_meeting", "agreed_date", "agreed_type", "agreed_venue", "file_code", "agreed_guests"];
const SALES_KEYS: WorkbookKey[] = ["evaluation", "ok", "closed", "follow_up", "status_note"];
const EXCEL_DATE_KEYS = new Set<string>(["visit_date", "came", "wanted_date", "follow_up", "mail_in", "mail_out", "commitment", "contract_date", "deposit_date", "final_meeting", "agreed_date"]);

export function workbookFrom(input: unknown): Record<string, string> {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};
  const source = input as Record<string, unknown>;
  const book: Record<string, string> = {};
  for (const key of WORKBOOK_KEYS) {
    const value = source[key];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    book[key] = trimmed.slice(0, 4000);
  }
  return book;
}

export function workbookBand(book: Record<string, string>): number {
  if (SERVICE_KEYS.some((key) => book[key])) return 4;
  if (COORDINATION_KEYS.some((key) => book[key])) return 3;
  if (SALES_KEYS.some((key) => book[key])) return 2;
  return 1;
}

export function foldVenue(value: string): string {
  return value.trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/κ/g, "k");
}

function codedVenueHints(value: string): string[] | null {
  if (value === "c" || value === "casa") return ["casa"];
  if (value === "r" || value === "regina") return ["regina"];
  if (value === "k" || value === "kthma" || value === "ktima") return ["kthma", "kτημα"];
  if (value === "μπ" || value === "mp") return ["μουσειο", "ποδηλ"];
  return null;
}

export function matchVenueId(venues: { id: string; name: string }[], raw: string | undefined): string | null {
  const value = foldVenue(raw ?? "");
  if (!value) return null;
  const exact = venues.find((venue) => foldVenue(venue.name) === value);
  if (exact) return exact.id;
  const hints = codedVenueHints(value);
  if (hints) {
    const coded = venues.filter((venue) => hints.some((hint) => foldVenue(venue.name).includes(hint)));
    return coded.length === 1 ? coded[0].id : null;
  }
  if (value.length < 3 || /^\d+$/.test(value)) return null;
  const partial = venues.filter((venue) => foldVenue(venue.name).includes(value));
  return partial.length === 1 ? partial[0].id : null;
}

export function cellText(key: string, value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  if (key === "visit_time") return excelTime(raw) ?? raw;
  if (EXCEL_DATE_KEYS.has(key)) return excelDate(raw) ?? raw;
  return raw;
}

function excelDate(value: string): string | null {
  const number = Number(value);
  if (!Number.isFinite(number) || !(number > 30000 && number < 80000)) return null;
  const ms = Date.UTC(1899, 11, 30) + Math.trunc(number) * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

function excelTime(value: string): string | null {
  const number = Number(value);
  if (!Number.isFinite(number) || !(number > 0 && number < 1)) return null;
  const minutes = roundHalfEven(number * 24 * 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function roundHalfEven(value: number): number {
  const floor = Math.floor(value);
  const fraction = value - floor;
  if (fraction > 0.5 + 1e-9) return floor + 1;
  if (fraction < 0.5 - 1e-9) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

function blank(value: string | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed || null;
}

function isoDate(value: string | undefined): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

/** Free children (up to 3.5) live in workbook_json. Missing means none, and old rows are left untouched. */
export function freeChildrenFromWorkbook(input: unknown): number | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = (input as Record<string, unknown>).children_free;
  const raw = typeof value === "number" ? String(value) : typeof value === "string" ? value.trim() : "";
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return parsed <= 100000 ? parsed : null;
}

function whole(value: string | undefined): number | null {
  const trimmed = (value ?? "").trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed <= 100000 ? parsed : null;
}

/** Digits only. Empty is missing. `min` is 1 for a headcount and 0 for a child count that may be zero. */
export function parseGuestCount(value: unknown, min: number): number | null {
  if (value == null || value === "") return null;
  const raw = typeof value === "number" ? (Number.isInteger(value) ? String(value) : "") : typeof value === "string" ? value.trim() : "";
  if (!/^\d+$/.test(raw)) throw new GuestCountError();
  const parsed = Number(raw);
  if (parsed < min || parsed > 100000) throw new GuestCountError();
  return parsed;
}

export class GuestCountError extends Error {
  constructor() {
    super("guest count");
    this.name = "GuestCountError";
  }
}

export function personName(values: { given_name?: string; family_name?: string; contact_name?: string }): string {
  const given = (values.given_name ?? "").trim();
  const family = (values.family_name ?? "").trim();
  if (given || family) return [family, given].filter(Boolean).join(" ");
  return (values.contact_name ?? "").trim();
}

export function nameParts(full: string, given?: string | null, family?: string | null): { given_name: string; family_name: string } {
  const storedGiven = (given ?? "").trim();
  const storedFamily = (family ?? "").trim();
  if (storedGiven || storedFamily) return { given_name: storedGiven, family_name: storedFamily };
  const trimmed = full.trim();
  const space = trimmed.indexOf(" ");
  if (space < 0) return { given_name: "", family_name: trimmed };
  return { family_name: trimmed.slice(0, space), given_name: trimmed.slice(space + 1).trim() };
}

export function eventFieldsFromEntry(values: Record<string, string>) {
  const normalized: Record<string, string> = { ...values };
  for (const key of EXCEL_DATE_KEYS) {
    if (key in values) normalized[key] = cellText(key, values[key]);
  }
  if ("visit_time" in values) normalized.visit_time = cellText("visit_time", values.visit_time);
  const workbook = workbookFrom(normalized);
  const visit = (normalized.visit_date ?? "").trim();
  if (visit) {
    workbook.visit_date = visit.slice(0, 4000);
    workbook.came = workbook.visit_date;
  } else {
    delete workbook.visit_date;
    delete workbook.came;
  }
  const start = isoDate(workbook.agreed_date) || isoDate(workbook.wanted_date) || isoDate(values.start_date);
  const contact_name = personName(values);
  return {
    given_name: blank(values.given_name),
    family_name: blank(values.family_name),
    contact_name,
    phone: blank(values.phone),
    email: blank(values.email),
    title: contact_name,
    event_type: blank(values.event_type),
    sales_stage: blank(values.sales_stage) || "new_inquiry",
    adults: whole(values.adults),
    children: whole(values.children),
    children_free: whole(values.children_free),
    area: blank(values.area),
    start_date: start,
    date_precision: start ? "exact" as const : "unknown" as const,
    venue_id: blank(values.venue_id),
    notes: workbook.status_note || workbook.requirements || null,
    language: "el" as const,
    workbook,
  };
}
